import { Spinner } from "@/components/Spinner";
import { blockage } from "@/endpoints/dryp/blockage";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { observer } from "mobx-react-lite";
import { FC, useEffect } from "react";
import { useParams } from "react-router";
import { DateTime, Duration } from "luxon";
import { AlertTriangle } from "lucide-react";
import { BlockageStatusBadge } from "@/components/badges/BlockageStatusBadge";

interface BlockageViewProps {
	blockage: Dryp.Blockage;
}
type DurationUnit = "years" | "months" | "weeks" | "days" | "hours" | "minutes" | "seconds";

const BlockageView: FC<BlockageViewProps> = ({ blockage }) => {
	const estBlockageInterval = (blockage?.estimatedNextBlockageInterval || 0) * 1000;
	const base = Duration.fromMillis(estBlockageInterval).shiftTo("days", "hours", "minutes", "seconds");

	const nonZero = (["days", "hours", "minutes"] as const).filter(u => base.get(u) >= 1);

	const shown = (nonZero.length ? nonZero : (["seconds"] as const)) satisfies readonly DurationUnit[];

	const isNegative = estBlockageInterval < 0;
	const absBase = isNegative ? base.negate() : base;

	const duration = absBase.shiftTo(...shown);
	const formattedCore = duration.toHuman({ maximumFractionDigits: 0, listStyle: "narrow" });
	const formattedDuration = isNegative ? `${formattedCore} ago` : formattedCore;

	return (
		<div className=" flex flex-col pt-3 space-y-3 max-w-96">
			{blockage?.estimatedNextBlockageTime && (
				<div className="flex items-center justify-between rounded-md border px-3 py-2 text-sm bg-muted/30">
					<span className="font-semibold text-foreground">Est. Next Blockage</span>
					<span className="text-muted-foreground">
						{DateTime.fromISO(String(blockage.estimatedNextBlockageTime)).toFormat("dd LLL yyyy, HH:mm")}
					</span>
				</div>
			)}

			<div className="flex items-center justify-between rounded-md border px-3 py-2 text-sm bg-muted/30">
				<span className="font-semibold text-foreground">Est Next Blockage Duration</span>
				<span className="text-muted-foreground">{formattedDuration}</span>
			</div>

			{blockage?.lastBlockageTime && (
				<div className="flex items-center justify-between rounded-md border px-3 py-2 text-sm bg-muted/30">
					<span className="font-semibold text-foreground">Last Blockage</span>
					<span className="text-muted-foreground">
						{DateTime.fromISO(String(blockage.lastBlockageTime)).toFormat("dd LLL yyyy, HH:mm")}
					</span>
				</div>
			)}

			<div className="flex items-center justify-between rounded-md border px-3 py-2 text-sm bg-muted/30">
				<span className="font-semibold text-foreground">Status</span>
				<span className="text-muted-foreground">
					<BlockageStatusBadge status={blockage.status.status as any} />
				</span>
			</div>
		</div>
	);
};

export const Blockage = observer(() => {
	const { id } = useParams<{ id: string }>();
	const structureId = id as Dryp.StructureId;

	const blockgeApi = useAsyncStateStore(blockage.getBlockage);
	useEffect(() => {
		blockgeApi.fetch(structureId);
	}, []);

	const blockageData = blockgeApi.data;

	return (
		<div>
			<h1 className="text-3xl font-bold text-gray-800">Blockage</h1>

			{blockgeApi.loading && <Spinner />}
			{blockgeApi?.error?.message && (
				<div className="w-full" aria-live="polite" aria-atomic="true">
					<div className="flex gap-3 items-start rounded-md  bg-red-50 border-red-200/80 px-3 py-3 w-full">
						<AlertTriangle className="h-4 w-4 mt-0.5 text-red-600 shrink-0" />
						<div className="flex-1 min-w-0">
							<div className="text-sm font-medium text-red-700">Data can't be calculated</div>

							{blockgeApi?.error?.message && (
								<details className="mt-1 group">
									<summary className="text-xs text-red-600/80 hover:text-red-700 cursor-pointer underline decoration-dotted">
										Show details
									</summary>
									<pre className="mt-1 max-h-28 overflow-auto whitespace-pre-wrap text-[11px] leading-snug text-red-700/90 bg-red-100/60 rounded p-2 border border-red-200/70 w-full">
										{blockgeApi?.error?.message}
									</pre>
								</details>
							)}
						</div>
					</div>
				</div>
			)}
			{blockageData && <BlockageView blockage={blockageData} />}
		</div>
	);
});
